import { useState, useEffect, useCallback } from 'react';
import {Card, Select, DatePicker, Table, Tag, Col, Row} from 'antd';
import { Button, Loading, Icon } from '~/components';
import { reportApi } from '~/api';
import { formatCurrency, handleRequest, apiError } from '~/utils';
import '../style/Report.scss';

const { Option } = Select;
const { RangePicker } = DatePicker;

function RevenueByUserReport()
{
    const [loading, setLoading] = useState(false);

    const [timeRange, setTimeRange] = useState('today');

    const [customDates, setCustomDates] = useState([]);

    const [data, setData] = useState(null);

    const loadReport = useCallback(async () => {

        setLoading(true);

        const params = { type: timeRange };

        if (timeRange === 'custom' && customDates.length === 2)
        {
            params.start_date = customDates[0].format('YYYY-MM-DD');

            params.end_date = customDates[1].format('YYYY-MM-DD');
        }

        const [error, response] = await handleRequest(reportApi.revenueByUser(params));

        setLoading(false);

        if (!apiError('Lỗi khi tải báo cáo', error, response)) {
            setData(response.data);
        }
    }, [timeRange, customDates]);

    useEffect(() => {
        loadReport();
    }, [loadReport]);

    const columns = [
        {
            title: 'STT',
            key: 'index',
            width: 60,
            align: 'center',
            render: (text, record, index) => index + 1,
        },
        {
            title: 'Người bán',
            dataIndex: 'username',
            key: 'username',
            render: (text, record) => (
                <div>
                    <div><strong>{record.name}</strong></div>
                    <div style={{ fontSize: 12, color: '#888' }}>{record.phone}</div>
                </div>
            ),
        },
        {
            title: 'Số đơn hàng',
            dataIndex: 'quantity',
            key: 'quantity',
            align: 'right',
            render: (value) => <Tag color="blue">{value}</Tag>,
        },
        {
            title: 'Doanh thu sản phẩm',
            dataIndex: 'subtotal',
            key: 'subtotal',
            align: 'right',
            render: (value) => <strong style={{ color: '#52c41a' }}>{formatCurrency(value)}</strong>,
        },
        {
            title: 'VAT',
            dataIndex: 'vat',
            key: 'vat',
            align: 'right',
            render: (value) => formatCurrency(value),
        },
        {
            title: 'Doanh thu',
            dataIndex: 'total',
            key: 'total',
            align: 'right',
            render: (value) => formatCurrency(value),
        }
    ];

    const totalRevenue = data?.summary?.subtotal || 0;

    const totalOrder = data?.summary?.orders || 0;

    const totalVat = data?.summary?.vat || 0;

    const total = data?.summary?.total || 0;

    return (
        <div className="container">
            <div className="report-container">
                <div className="report-header">
                    <h1>Báo cáo Doanh thu theo Người bán</h1>
                    <div className="report-filters">
                        <Select
                            value={timeRange}
                            onChange={setTimeRange}
                            style={{ width: 200 }}
                        >
                            <Option value="today">Hôm nay</Option>
                            <Option value="yesterday">Hôm qua</Option>
                            <Option value="this_week">Tuần này</Option>
                            <Option value="last_week">Tuần trước</Option>
                            <Option value="this_month">Tháng này</Option>
                            <Option value="last_month">Tháng trước</Option>
                            <Option value="this_year">Năm nay</Option>
                            <Option value="custom">Tùy chọn</Option>
                        </Select>

                        {timeRange === 'custom' && (
                            <RangePicker
                                value={customDates}
                                onChange={setCustomDates}
                                format="DD/MM/YYYY"
                                style={{ marginLeft: 10 }}
                            />
                        )}

                        <Button
                            primary
                            onClick={loadReport}
                            style={{ marginLeft: 10 }}
                            disabled={timeRange === 'custom' && customDates.length !== 2}
                        >
                            Xem báo cáo
                        </Button>
                    </div>
                </div>

                {loading && <Loading />}

                {!loading && data && (
                    <>
                        <Row gutter={16} className="report-summary">
                            <Col xs={24} sm={12} lg={6}>
                                <Card className="stat-card stat-card-cost">
                                    <div className="stat-icon">
                                        <div className="icon-wrapper cost-icon">
                                            <span className="icon-text">₫</span>
                                        </div>
                                    </div>
                                    <div className="stat-content">
                                        <div className="stat-title">Đơn hàng</div>
                                        <div className="stat-value">{totalOrder}</div>
                                        <div className="stat-trend negative">Tổng đơn hàng bán được</div>
                                    </div>
                                </Card>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                                <Card className="stat-card stat-card-revenue">
                                    <div className="stat-icon">
                                        <div className="icon-wrapper revenue-icon">
                                            <span className="icon-text">₫</span>
                                        </div>
                                    </div>
                                    <div className="stat-content">
                                        <div className="stat-title">Doanh thu</div>
                                        <div className="stat-value">{formatCurrency(totalRevenue)}</div>
                                        <div className="stat-trend positive">Doanh thu chưa bao gồm thuế</div>
                                    </div>
                                </Card>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                                <Card className="stat-card stat-card-profit">
                                    <div className="stat-icon">
                                        <div className="icon-wrapper profit-icon">
                                            {Icon.chartLine}
                                        </div>
                                    </div>
                                    <div className="stat-content">
                                        <div className="stat-title">Thuế</div>
                                        <div className="stat-value">{formatCurrency(totalVat)}</div>
                                        <div className="stat-trend positive">Tổng chi phí thuế</div>
                                    </div>
                                </Card>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                                <Card className="stat-card stat-card-margin">
                                    <div className="stat-icon">
                                        <div className="icon-wrapper margin-icon">
                                            <span className="icon-text">%</span>
                                        </div>
                                    </div>
                                    <div className="stat-content">
                                        <div className="stat-title">Tổng</div>
                                        <div className="stat-value">{formatCurrency(total)}</div>
                                        <div className="stat-trend">Tổng doanh thu</div>
                                    </div>
                                </Card>
                            </Col>
                        </Row>

                        <Card title="Chi tiết theo người bán" className="table-card">
                            <Table
                                columns={columns}
                                dataSource={data.details}
                                rowKey="user_id"
                                pagination={{
                                    pageSize: 20,
                                    showSizeChanger: true,
                                    showTotal: (total) => `Tổng ${total} người bán`
                                }}
                            />
                        </Card>
                    </>
                )}
            </div>
        </div>
    );
}

export default RevenueByUserReport;
